import '../components/customer-checklist-work.css';
import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, propertyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, Select, TextArea, TextInput } from '../components/ui.tsx';
import AddressAutocomplete from '../components/address-autocomplete.tsx';
import { useAppConfig } from '../components/turnstile.tsx';
import { useCommercialWork } from '../components/commercial-gate.tsx';
import { MoneyInput } from '../components/money-input.tsx';
import { useSession } from '../state/session.tsx';
import { OCCUPANCY_STATUSES, PROPERTY_TYPES } from '@shared/constants/index.ts';
import type { Property } from '@shared/types/index.ts';

interface PropertyFormProps {
  customerId: string;
  property?: Property;
  onSaved: (property: Property) => void;
  onCancel?: () => void;
  captureSaveView?: () => () => boolean;
  preventCancelWhileSaving?: boolean;
  onDraftStarted?: () => void;
}

/** Empty string means "not answered"; the API treats it as null. */
function numberOrNull(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function PropertyForm({
  customerId,
  property,
  onSaved,
  onCancel,
  captureSaveView,
  preventCancelWhileSaving = false,
  onDraftStarted,
}: PropertyFormProps) {
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  const queryClient = useQueryClient();
  const { company } = useSession();
  const [areaRateCents, setAreaRateCents] = useState<number | null>(property?.areaRateCents ?? null);
  const [form, setForm] = useState(() => ({
    name: property?.name ?? '',
    propertyType: property?.propertyType ?? 'house',
    addressLine1: property?.addressLine1 ?? '',
    addressLine2: property?.addressLine2 ?? '',
    city: property?.city ?? '',
    state: property?.state ?? '',
    postalCode: property?.postalCode ?? '',
    squareFeet: property?.squareFeet?.toString() ?? '',
    bedrooms: property?.bedrooms?.toString() ?? '',
    bathrooms: property?.bathrooms?.toString() ?? '',
    floors: property?.floors?.toString() ?? '',
    occupancyStatus: property?.occupancyStatus ?? '',
    petsDescription: property?.petsDescription ?? '',
    accessNotes: property?.accessNotes ?? '',
    parkingNotes: property?.parkingNotes ?? '',
    siteCode: property?.siteCode ?? '',
    siteContactName: property?.siteContactName ?? '',
    siteContactTitle: property?.siteContactTitle ?? '',
    siteContactEmail: property?.siteContactEmail ?? '',
    siteContactPhone: property?.siteContactPhone ?? '',
  }));
  /**
   * Where the property is, for the clock-in distance check — held apart from
   * the text boxes because it never comes from them. It arrives only with a
   * picked suggestion, and it goes the moment any of the boxes a suggestion
   * fills is edited by hand: the stored point describes the address the
   * provider matched, not whatever that address was typed into afterwards
   * (see `Property.latitude`). Null is saved as null, which clears a point a
   * hand-corrected address no longer stands behind.
   */
  const [coordinates, setCoordinates] = useState<{ latitude: number | null; longitude: number | null }>(() => ({
    latitude: property?.latitude ?? null,
    longitude: property?.longitude ?? null,
  }));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const commercialWork = useCommercialWork();
  /**
   * Whether this deployment suggests addresses at all, which is what decides
   * the order of the address boxes below.
   *
   * Settled once, when the form opens, and then left alone for as long as it
   * stays open. Reordering the boxes moves their nodes, and a node that moves
   * takes the focus and the half-typed keystroke inside it with it — so an
   * order that could still change is one that can throw away an estimator's
   * typing the moment a slow `/config` lands.
   *
   * Deciding early costs nothing in practice: `/config` is fetched once for
   * the whole session, and this form is only ever reached by a click within
   * the app, by which time the answer is long since cached. Should it somehow
   * open first, the boxes keep the plain street-first order they have always
   * had — the one that works without a provider — for as long as it is open.
   */
  const suggesting = useAppConfig().data?.addressAutocomplete === true;
  const [cityFirst] = useState(suggesting);
  /**
   * Whether this form names the site or its own contact.
   *
   * Read from the DRAFT, which is seeded from the saved property: a site that
   * already carries a code shows the block as it always did, and a code typed
   * under commercial mode keeps it open if the session refreshes to
   * residential mid-edit. Reading the saved record alone would take the block
   * off screen while `form` went on submitting the contact typed into it.
   *
   * Nothing appears as it is typed, because a hidden block cannot be typed
   * into: the only way in is to already carry an answer.
   */
  const carriesSiteDetails = Boolean(
    form.siteCode.trim() ||
    form.siteContactName.trim() ||
    form.siteContactTitle.trim() ||
    form.siteContactEmail.trim() ||
    form.siteContactPhone.trim(),
  );

  function set(key: keyof typeof form) {
    return (event: { target: { value: string } }) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  /**
   * Like `set`, for the boxes that place the property on the map. Typing in
   * one of them takes the coordinates with it — `addressLine2` deliberately
   * uses plain `set`, because an apartment number does not move the building.
   */
  function setGeocoded(key: 'city' | 'state' | 'postalCode') {
    return (event: { target: { value: string } }) => {
      setCoordinates({ latitude: null, longitude: null });
      setForm((current) => ({ ...current, [key]: event.target.value }));
    };
  }

  const mutation = useMutation({
    mutationFn: (attempt: {
      payload: Record<string, unknown>;
      customerId: string;
      propertyId?: string;
      isCurrent: () => boolean;
    }) => {
      if (!attempt.isCurrent()) throw new Error('The property save view changed before submission.');
      return attempt.propertyId
        ? propertyApi.update(attempt.propertyId, attempt.payload)
        : customerApi.createProperty(attempt.customerId, attempt.payload);
    },
    onSuccess: (saved, attempt) => {
      void queryClient.invalidateQueries({ queryKey: ['customer-properties', attempt.customerId] });
      if (attempt.isCurrent()) onSaved(saved);
    },
    onError: (caught, attempt) => {
      if (!attempt.isCurrent()) return;
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setError(Object.keys(caught.fields).length > 0 ? null : caught.message);
      } else {
        setError(t('propertyForm.saveError'));
      }
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (mutation.isPending) return;
    onDraftStarted?.();
    setError(null);
    setFieldErrors({});
    mutation.mutate({
      customerId,
      propertyId: property?.id,
      isCurrent: captureSaveView?.() ?? (() => true),
      payload: {
        name: form.name,
        propertyType: form.propertyType,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        squareFeet: numberOrNull(form.squareFeet),
        // Sent only when changed, so an unrelated edit never resubmits a stale rate.
        ...(areaRateCents !== (property?.areaRateCents ?? null)
          ? { areaRateCents: areaRateCents && areaRateCents > 0 ? areaRateCents : null }
          : {}),
        bedrooms: numberOrNull(form.bedrooms),
        bathrooms: numberOrNull(form.bathrooms),
        floors: numberOrNull(form.floors),
        occupancyStatus: form.occupancyStatus || null,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        petsDescription: form.petsDescription,
        accessNotes: form.accessNotes,
        parkingNotes: form.parkingNotes,
        siteCode: form.siteCode,
        siteContactName: form.siteContactName,
        siteContactTitle: form.siteContactTitle,
        siteContactEmail: form.siteContactEmail,
        siteContactPhone: form.siteContactPhone,
      },
    });
  }

  /**
   * The two halves of the address, kept as values so the one that has to come
   * first can come first.
   *
   * Keyed, and that is not decoration: reordering unkeyed siblings would have
   * React reuse the input already at each position and change what it holds,
   * which would move somebody's focus and their half-typed text to another
   * box. Keys make it move the boxes themselves.
   */
  const street = (
    <Fragment key="street">
      <AddressAutocomplete
        label={t('propertyForm.addressLine1')}
        value={form.addressLine1}
        // Whatever is in the three locality boxes travels with the lookup. A
        // street on its own is ambiguous across every town that has one, so
        // this is what makes a match possible at all rather than a refinement
        // of one.
        context={{ city: form.city, state: form.state, postalCode: form.postalCode }}
        onChange={(addressLine1) => {
          // Typing here, like in any of the locality boxes, parts the address
          // from the point the provider matched.
          setCoordinates({ latitude: null, longitude: null });
          setForm((current) => ({ ...current, addressLine1 }));
        }}
        onSelect={(suggestion) => {
          onDraftStarted?.();
          // The one way coordinates get in: the provider said where the
          // address it matched is. A suggestion that came without them clears
          // whatever an earlier pick left, because the address on the form is
          // no longer the one that point described.
          setCoordinates({ latitude: suggestion.latitude ?? null, longitude: suggestion.longitude ?? null });
          setForm((current) => ({
            ...current,
            addressLine1: suggestion.addressLine1,
            // Only overwritten where the suggestion has something to say. A
            // half-matched address that came back without a postcode must not
            // blank one the estimator already typed.
            city: suggestion.city || current.city,
            state: suggestion.state || current.state,
            postalCode: suggestion.postalCode || current.postalCode,
          }));
        }}
      />
      <TextInput label={t('propertyForm.addressLine2')} value={form.addressLine2} onChange={set('addressLine2')} />
    </Fragment>
  );

  const locality = (
    <div key="locality" className="grid gap-x-4 sm:grid-cols-3">
      <TextInput
        label={t('propertyForm.city')}
        // Only where it is the box being asked for first, and only then
        // because a box that has moved above the street for no stated reason
        // reads as a mistake in the form.
        hint={cityFirst ? t('propertyForm.cityFirstHint') : undefined}
        value={form.city}
        onChange={setGeocoded('city')}
      />
      <TextInput label={t('propertyForm.state')} value={form.state} onChange={setGeocoded('state')} />
      <TextInput label={t('propertyForm.postalCode')} value={form.postalCode} onChange={setGeocoded('postalCode')} />
    </div>
  );

  return (
    <form className="customer-work-form" onSubmit={handleSubmit} onChangeCapture={onDraftStarted} noValidate>
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <fieldset className="customer-work-panel">
        <legend>{es ? 'Dirección de servicio' : 'Service address'}</legend>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('propertyForm.nickname')}
            hint={t('propertyForm.nicknameHint')}
            value={form.name}
            onChange={set('name')}
          />
          <Select
            label={t('propertyForm.propertyType')}
            required
            value={form.propertyType}
            error={fieldErrors.propertyType}
            onChange={set('propertyType')}
            options={PROPERTY_TYPES.map((type) => ({ value: type, label: t(`property.type.${type}`) }))}
          />
        </div>

        {/*
        Taking a suggestion fills the city, state and postcode too — that is
        the point of it, and typing the street again into three more boxes is
        most of the work of adding a property. Every one of them stays editable
        afterwards, because the provider is a convenience and not an authority
        on where this customer actually lives.

        Which of the two comes first is the whole difference between a
        suggestion box that works and one that appears to be broken. The
        default provider matches an address nationally rather than completing a
        fragment, so a street typed before its town is unmatchable — and an
        estimator who fills the boxes top to bottom would spend that first
        lookup on nothing. Asked for the town first, the very first lookup the
        street box makes is one that can be answered.

        Ordered on whether suggestions are on at all rather than on which
        provider is behind them: the browser is deliberately never told which
        service a deployment uses (see routes/addresses.ts), and a locality
        typed up front helps the fragment-completing provider too. With no
        provider configured these are four plain text boxes and the familiar
        street-first order is left exactly as it was.
      */}
        {[cityFirst ? locality : street, cityFirst ? street : locality]}
      </fieldset>
      <fieldset className="customer-work-panel">
        <legend>{es ? 'Detalles de la propiedad' : 'Property details'}</legend>
        <div className="grid gap-x-4 sm:grid-cols-4">
          <TextInput
            label={t('propertyForm.squareFeet')}
            type="number"
            min={0}
            value={form.squareFeet}
            onChange={set('squareFeet')}
          />
          <TextInput
            label={t('propertyForm.bedrooms')}
            type="number"
            min={0}
            step={0.5}
            value={form.bedrooms}
            onChange={set('bedrooms')}
          />
          <TextInput
            label={t('propertyForm.bathrooms')}
            type="number"
            min={0}
            step={0.5}
            value={form.bathrooms}
            onChange={set('bathrooms')}
          />
          <TextInput
            label={t('propertyForm.floors')}
            type="number"
            min={0}
            value={form.floors}
            onChange={set('floors')}
          />
        </div>
        <MoneyInput
          label={t('propertyForm.areaRate')}
          hint={t('propertyForm.areaRateHint')}
          currencyCode={company?.currencyCode}
          valueCents={areaRateCents}
          min={0}
          onChangeCents={setAreaRateCents}
        />

        <Select
          label={t('propertyForm.occupancy')}
          value={form.occupancyStatus}
          onChange={set('occupancyStatus')}
          options={[
            { value: '', label: t('propertyForm.occupancyNotSpecified') },
            ...OCCUPANCY_STATUSES.map((status) => ({ value: status, label: t(`property.occupancy.${status}`) })),
          ]}
        />

        <TextInput
          label={t('propertyForm.pets')}
          hint={t('propertyForm.petsHint')}
          value={form.petsDescription}
          onChange={set('petsDescription')}
        />
      </fieldset>
      <fieldset className="customer-work-panel">
        <legend>{t('propertyForm.accessInstructions')}</legend>
        <TextArea
          label={t('propertyForm.accessInstructions')}
          rows={2}
          value={form.accessNotes}
          onChange={set('accessNotes')}
        />
        <TextArea
          label={t('propertyForm.parkingNotes')}
          rows={2}
          value={form.parkingNotes}
          onChange={set('parkingNotes')}
        />

        {/*
        The site's own person, on a multi-site account.

        Deliberately its own block rather than four more boxes in the run
        above: this is a different PERSON from the one on the customer record,
        and the heading is what stops somebody typing the billing contact in
        again. Leave it empty and nothing changes — visit notices go to the
        customer's address, as they always have.

        Commercial work only. A house has no facilities manager and no client
        reference like "Store 0392"; the person who books the clean is the
        person who lives there, and this block asked a housecleaner to tell
        two of them apart. Kept on screen where the property already carries
        an answer, so a company that switched back to residential can still
        edit what it recorded.
      */}
      </fieldset>
      {commercialWork || carriesSiteDetails ? (
        <fieldset className="rounded-md border border-[color:var(--color-line)] p-4">
          <legend className="px-1 text-sm font-semibold">{t('propertyForm.siteContactHeading')}</legend>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('propertyForm.siteContactHint')}</p>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <TextInput
              label={t('propertyForm.siteCode')}
              hint={t('propertyForm.siteCodeHint')}
              value={form.siteCode}
              error={fieldErrors.siteCode}
              onChange={set('siteCode')}
            />
            <TextInput
              label={t('propertyForm.siteContactName')}
              value={form.siteContactName}
              error={fieldErrors.siteContactName}
              onChange={set('siteContactName')}
            />
            <TextInput
              label={t('propertyForm.siteContactTitle')}
              value={form.siteContactTitle}
              error={fieldErrors.siteContactTitle}
              onChange={set('siteContactTitle')}
            />
            <TextInput
              label={t('propertyForm.siteContactEmail')}
              hint={t('propertyForm.siteContactEmailHint')}
              type="email"
              value={form.siteContactEmail}
              error={fieldErrors.siteContactEmail}
              onChange={set('siteContactEmail')}
            />
            <TextInput
              label={t('propertyForm.siteContactPhone')}
              type="tel"
              value={form.siteContactPhone}
              error={fieldErrors.siteContactPhone}
              onChange={set('siteContactPhone')}
            />
          </div>
        </fieldset>
      ) : null}

      <div className="flex gap-3">
        <Button type="submit" variant="primary" busy={mutation.isPending}>
          {property ? t('propertyForm.saveChanges') : t('propertyForm.addProperty')}
        </Button>
        {onCancel ? (
          <Button onClick={onCancel} disabled={preventCancelWhileSaving && mutation.isPending}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
